import { omit } from "lodash"
import { IWorkerLocalStorage } from "./type"
import { EncryptionKeys } from "controllers/EncryptionController"
import { CHUNK_SIZE_ENCRYPT, encryptionGCMHelper } from "helpers/encryption-gcm"

export const getWorkerLocalStorageData = (): IWorkerLocalStorage => {
  return {
    currentProjectEncryptionKey: localStorage.getItem(
      EncryptionKeys.currentProjectEncryptionKey
    ) as string,
    projectEncryptionKeys: JSON.parse(
      localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
    ),
    currentConversationEncryptionKey: localStorage.getItem(
      EncryptionKeys.currentConversationEncryptionKey
    ) as string,
    conversationEncryptionKeys: JSON.parse(
      localStorage.getItem(EncryptionKeys.conversationEncryptionKeys) || "{}"
    ),
    userVaultKey: localStorage.getItem(EncryptionKeys.userVaultKey) as string,
    buildAdditionalInfoEncryptionKeys: JSON.parse(
      localStorage.getItem(EncryptionKeys.buildAdditionalInfoEncryptionKeys) ||
        "{}"
    ),
    userId: localStorage.getItem("user_id") as string,
  }
}

export const encryptSVGs = async (
  dataSvgs: any[],
  token: string,
  componentHistoryId: string,
  fileKey: string
) => {
  const svgRequest: { fileName: string }[] = []
  console.log("-------START ENCRYPT SVG FILE-----------")

  for (const svg of dataSvgs) {
    const fileName = `${svg.name}.svg`
    const svgBlob = new Blob([svg.content], { type: "image/svg+xml" })
    const svgFile = new File([svgBlob], fileName, { type: "image/svg+xml" })
    const totalChunks = Math.ceil(svgFile.size / CHUNK_SIZE_ENCRYPT)
    const multipartUploadPath =
      await encryptionGCMHelper.getMultipartUploadPath(token, {
        fileName: fileName,
        fileType: "image/svg+xml",
        totalChunks,
        componentHistoryId,
        componentType: "pcb/gerber/layers",
      })
    const encryptKey = await encryptionGCMHelper.importAESKey(fileKey)

    if (!multipartUploadPath?.key || !multipartUploadPath?.uploadId) {
      console.log(`${fileName} - Failed to get multipart upload path`)
      continue
    }
    const etags = await encryptionGCMHelper.createEncryptedChunksStream(
      svgFile,
      encryptKey,
      CHUNK_SIZE_ENCRYPT,
      multipartUploadPath,
      totalChunks
    )
    const mergeSuccess = await encryptionGCMHelper.mergeUploadedChunks(
      token,
      multipartUploadPath,
      etags
    )
    if (!mergeSuccess) {
      console.log(`${svg.name} - Failed to merge encrypted chunks`)

      continue
    }

    svgRequest.push({
      fileName: svg.name,
    })
  }
  console.log("Total successfully encrypted files:", svgRequest.length)
  console.log("-------END ENCRYPT SVG FILE-----------")
  return { svgRequest, svgs: dataSvgs.map((svg) => omit(svg, "content")) }
}

const isFileReadable = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    const reader = new FileReader()
    const tinyChunk = file.slice(0, 1)

    reader.onload = () => resolve(true)
    reader.onerror = () => resolve(false)

    try {
      reader.readAsArrayBuffer(tinyChunk)
    } catch (err) {
      resolve(false)
    }
  })
}
export const encryptFiles = async (
  files: any[],
  isRawFile: boolean,
  fileKey: string,
  token: string,
  componentHistoryId: string,
  componentType: string,
  userId: string
) => {
  console.log("-------START ENCRYPT FILE-----------")

  const privateKey = await encryptionGCMHelper.getUserPrivateKey(userId)
  if (!privateKey) {
    return {
      newFileHash: [],
      attachments: [],
      fileErrors: ["Private key not found"],
    }
  }

  const encryptKey = await encryptionGCMHelper.importAESKey(fileKey)
  const newFileHash: any[] = []
  const fileErrors: string[] = []

  for (const file of files) {
    const fileName = isRawFile ? file.name : file.file.name
    const fileNameError = isRawFile ? file.name : file.file_name
    const originFile = isRawFile ? file : file.file
    const totalChunks = Math.ceil(originFile.size / CHUNK_SIZE_ENCRYPT)

    try {
      console.log("Encrypting file:", fileName)

      if (!(await isFileReadable(originFile))) {
        fileErrors.push(
          `${fileNameError} - File is no longer available or has been deleted`
        )
        continue
      }

      const hashHex = await encryptionGCMHelper.getFileHash(originFile)
      const hashBuffer = new TextEncoder().encode(hashHex)
      const signature = await encryptionGCMHelper.createSignature(
        privateKey,
        hashBuffer
      )
      const signatureBase64 = encryptionGCMHelper.bufferToBase64(signature)

      const multipartUploadPath =
        await encryptionGCMHelper.getMultipartUploadPath(token, {
          fileName,
          fileType: originFile.type || "application/octet-stream",
          totalChunks,
          componentHistoryId,
          componentType,
        })

      if (!multipartUploadPath?.key || !multipartUploadPath?.uploadId) {
        fileErrors.push(
          `${fileNameError} - Failed to get multipart upload path`
        )
        continue
      }

      const etags = await encryptionGCMHelper.createEncryptedChunksStream(
        originFile,
        encryptKey,
        CHUNK_SIZE_ENCRYPT,
        multipartUploadPath,
        totalChunks
      )

      const mergeSuccess = await encryptionGCMHelper.mergeUploadedChunks(
        token,
        multipartUploadPath,
        etags
      )
      if (!mergeSuccess) {
        fileErrors.push(`${fileNameError} - Failed to merge encrypted chunks`)
        continue
      }

      newFileHash.push({
        file_name: fileName,
        file_hash: signatureBase64,
        rootURL: multipartUploadPath.rootURL,
      })
    } catch (error) {
      console.error("Encryption error:", error)
      fileErrors.push(`${fileNameError} - Error encrypting file`)
    }
  }

  console.log("Total successfully encrypted files:", newFileHash.length)
  console.log("-------END ENCRYPT FILE-----------")

  return {
    newFileHash: newFileHash.filter((el) => el.file_hash),
    fileErrors,
  }
}
export const toUint8Array = (data: any) => {
  const dataArray = new Uint8Array(data.sigBytes)
  for (let i = 0x0; i < data.sigBytes; i++) {
    dataArray[i] = (data.words[i >>> 0x2] >>> (0x18 - (i % 0x4) * 0x8)) & 0xff
  }
  return new Uint8Array(dataArray)
}
